import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FieldCheck = ({ ok }) =>
  ok ? (
    <span className="text-success text-body-xs ml-1" aria-label="Complete">✓</span>
  ) : (
    <span className="text-on-background/30 text-body-xs ml-1" aria-label="Incomplete">○</span>
  );

export default function Signup() {
  const { t } = useTranslation('app');
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userWellisId, setUserWellisId] = useState('');
  const [wellisIdError, setWellisIdError] = useState('');
  const [wellisIdAvailable, setWellisIdAvailable] = useState(null);
  const [checkingWellisId, setCheckingWellisId] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [affiliateName, setAffiliateName] = useState('');
  const [affiliateError, setAffiliateError] = useState('');
  const [validatingAffiliate, setValidatingAffiliate] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(''); // 'weak', 'fair', 'good', 'strong'
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const navigate = useNavigate();

  const validatePasswordStrength = (pwd) => {
    if (pwd.length < 8) return 'weak';
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /\d/.test(pwd);
    const hasSpecial = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(pwd);

    const strengthScore = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (strengthScore < 2) return 'weak';
    if (strengthScore === 2) return 'fair';
    if (strengthScore === 3) return 'good';
    return 'strong';
  };

  const getPasswordStrengthColor = () => {
    const colors = {
      weak: 'bg-error',
      fair: 'bg-warning',
      good: 'bg-info',
      strong: 'bg-success',
    };
    return colors[passwordStrength] || 'bg-outline';
  };

  const getPasswordStrengthText = () => {
    const texts = {
      weak: t('auth.signup.weak'),
      fair: t('auth.signup.fair'),
      good: t('auth.signup.good'),
      strong: t('auth.signup.strong'),
    };
    return texts[passwordStrength] || '';
  };

  // Pre-fill referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
    }
    const isResend = searchParams.get('resend') === 'true';
    if (isResend) {
      setIsResending(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (password) {
      setPasswordStrength(validatePasswordStrength(password));
    } else {
      setPasswordStrength('');
    }
  }, [password]);

  useEffect(() => {
    setPasswordsMatch(password === confirmPassword || confirmPassword === '');
  }, [password, confirmPassword]);

  // Validate affiliate code when it changes
  useEffect(() => {
    if (!referralCode.trim()) {
      setAffiliateName('');
      setAffiliateError('');
      return;
    }

    const validateAffiliateCode = async () => {
      setValidatingAffiliate(true);
      setAffiliateError('');
      setAffiliateName('');

      try {
        console.log('[Affiliate Validation] Starting validation for:', referralCode.trim());
        console.log('[Affiliate Validation] API URL:', process.env.REACT_APP_API_URL);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/validate-affiliate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliate_code: referralCode.trim() }),
        });

        console.log('[Affiliate Validation] Response status:', response.status, response.ok);

        const data = await response.json();
        console.log('[Affiliate Validation] Response data:', data);

        if (!response.ok) {
          console.log('[Affiliate Validation] Response not OK, error:', data.error);
          setAffiliateError(data.error || 'Invalid affiliate code');
        } else if (data.success) {
          console.log('[Affiliate Validation] Success! Affiliate:', data.affiliate_name);
          setAffiliateName(data.affiliate_name);
          setAffiliateError('');
        } else {
          console.log('[Affiliate Validation] Unexpected response:', data);
          setAffiliateError(data.message || 'Failed to validate affiliate code');
        }
      } catch (err) {
        console.error('[Affiliate Validation] Exception:', err);
        setAffiliateError(err.message || 'Failed to validate affiliate code');
      } finally {
        setValidatingAffiliate(false);
      }
    };

    const timer = setTimeout(validateAffiliateCode, 500); // Debounce validation
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleGoogleSignup = () => {
    // Validate referral code before initiating Google OAuth
    if (!referralCode.trim() || !affiliateName) {
      setError(t('auth.signup.validAffiliateFirst'));
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError(t('auth.signup.googleNotConfigured'));
      return;
    }

    // Pass referral code as state parameter in OAuth flow
    const state = btoa(JSON.stringify({
      referralCode: referralCode.trim(),
      type: 'signup'
    }));

    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const scope = 'openid email profile';

    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(scope)}&` +
      `state=${encodeURIComponent(state)}`;

    window.location.href = googleAuthUrl;
  };

  const validateEmail = (emailStr) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailStr);
  };

  const checkWellisIdUniqueness = async () => {
    if (!userWellisId || userWellisId.length !== 10) {
      setWellisIdError(t('auth.signup.wellisIdFormat'));
      return;
    }

    setCheckingWellisId(true);
    setWellisIdError('');
    setWellisIdAvailable(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check-wellis-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wellis_id: `WELLIS-${userWellisId.toUpperCase()}` }),
      });

      const data = await response.json();

      if (!response.ok) {
        setWellisIdError(data.error || t('auth.signup.wellisIdCheckFailed'));
        setWellisIdAvailable(false);
      } else if (data.available) {
        setWellisIdAvailable(true);
        setWellisIdError('');
      } else {
        setWellisIdAvailable(false);
        setWellisIdError(t('auth.signup.wellisIdTaken'));
      }
    } catch (err) {
      setWellisIdError(err.message || t('auth.signup.wellisIdCheckFailed'));
      setWellisIdAvailable(false);
    } finally {
      setCheckingWellisId(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.toLowerCase().trim();
    const trimmedPhone = phone.trim();

    // Either email or phone must be provided
    if (!trimmedEmail && !trimmedPhone) {
      setError(t('auth.signup.emailOrPhoneRequired'));
      return;
    }

    // If email is provided, validate it
    if (trimmedEmail && !validateEmail(trimmedEmail)) {
      setError(t('auth.signup.invalidEmail'));
      return;
    }

    if (!referralCode.trim()) {
      setError(t('auth.signup.referralRequired'));
      return;
    }

    if (!firstName.trim()) {
      setError(t('auth.signup.firstNameRequired'));
      return;
    }

    if (!userWellisId.trim()) {
      setError(t('auth.signup.wellisIdRequired'));
      return;
    }

    if (userWellisId.length !== 10) {
      setError(t('auth.signup.wellisIdFormat'));
      return;
    }

    if (!wellisIdAvailable) {
      setError(t('auth.signup.wellisIdNotVerified'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.signup.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: trimmedEmail || null,
          phone: trimmedPhone || null,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          wellis_id: userWellisId.trim().toUpperCase(),
          referral_code: referralCode.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Email/password signup: redirect to login with pre-filled credentials
      // (Google OAuth auto-authenticates during callback)
      if (data.approved) {
        navigate('/login', { state: {
          wellisId: data.affiliate_code,
          email: trimmedEmail,
          message: 'Account created! Please login with your credentials.'
        } });
      } else {
        navigate('/signup-pending', { state: { email: trimmedEmail } });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim()) {
      setError(t('auth.signup.emailRequired'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      navigate('/dashboard', { state: { email, showResendConfirmation: true } });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isResending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
        <div className="w-full max-w-md">
          <h1 className="text-headline-lg text-on-background mb-2 text-center">{t('auth.signup.resendEmail')}</h1>
          <p className="text-body-md text-on-background/60 text-center mb-8">
            {t('auth.signup.resendEmailDesc')}
          </p>

          {error && (
            <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="resend-email" className="block text-body-sm text-on-background mb-1">
                {t('auth.signup.email')}
              </label>
              <input
                id="resend-email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
                placeholder="your@email.com"
                autoComplete="email"
              />
            </div>

            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-body-md"
            >
              {loading ? t('auth.signup.sending') : t('auth.signup.sendVerification')}
            </button>

            <button
              onClick={() => {
                setIsResending(false);
                setError('');
                setEmail('');
              }}
              className="w-full bg-surface border border-outline text-on-surface font-medium py-2 rounded-lg hover:bg-surface/80 transition-colors text-body-md"
            >
              {t('auth.signup.backToSignup')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
      <div className="w-full max-w-md">
        <h1 className="text-headline-lg text-on-background mb-2 text-center">{t('auth.signup.title')}</h1>
        <p className="text-body-md text-on-background/60 text-center mb-8">{t('auth.signup.subtitle')}</p>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            <p>{error}</p>
            <p className="mt-2 text-body-xs">
              <a href="/forgot-password" className="underline hover:text-error/80">
                {t('auth.signup.forgotPassword')}
              </a>
            </p>
          </div>
        )}

        <form onSubmit={handleEmailSignup} className="space-y-4">
          {/* Referral Code Field - Moved to top */}
          <div>
            <label htmlFor="referralCode" className="block text-body-sm text-on-background mb-1">
              {t('auth.signup.referralCode')} <span className="text-error">*</span>
              <FieldCheck ok={!!affiliateName} />
            </label>
            <input
              id="referralCode"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              className={`w-full px-4 py-2 bg-surface border rounded-lg text-on-surface text-body-sm focus:outline-none ${
                affiliateError ? 'border-error' : affiliateName ? 'border-success' : 'border-outline'
              } focus:border-primary`}
              placeholder="WELLIS-XXXXXXXX"
              required
            />
            {validatingAffiliate && (
              <p className="text-body-xs text-on-background/60 mt-2">{t('auth.signup.validatingAffiliate')}</p>
            )}
            {affiliateError && (
              <p className="text-body-xs text-error mt-2">{affiliateError}</p>
            )}
            {affiliateName && (
              <div className="mt-3 p-3 bg-success/10 border border-success rounded-lg">
                <p className="text-body-sm text-success font-medium">{t('auth.signup.signingUnder')} <strong>{affiliateName}</strong></p>
              </div>
            )}
          </div>

          {/* Google Signup Button - Always visible, enabled after valid referral code */}
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={!affiliateName || validatingAffiliate || loading}
            className={`w-full px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
              affiliateName && !validatingAffiliate
                ? 'bg-surface border border-outline text-on-surface hover:bg-surface/80 cursor-pointer'
                : 'bg-surface/50 border border-outline/50 text-on-surface/50 cursor-not-allowed'
            }`}
            title={!affiliateName ? t('auth.signup.validAffiliateFirst') : validatingAffiliate ? t('auth.signup.validatingAffiliate') : t('auth.signup.googleButton')}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.signup.googleButton')}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-outline-variant/30"></div>
            <span className="text-body-sm text-on-background/60">{t('auth.signup.or')}</span>
            <div className="flex-1 h-px bg-outline-variant/30"></div>
          </div>

          <div>
            <label htmlFor="userWellisId" className="block text-body-sm text-on-background mb-1">
              {t('auth.signup.yourWellisId')} <span className="text-error">*</span>
              <FieldCheck ok={wellisIdAvailable === true} />
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <div className="flex items-center px-4 py-2 bg-surface border rounded-lg border-outline text-on-surface text-body-sm">
                  <span className="text-on-background/60 mr-2">WELLIS-</span>
                  <input
                    id="userWellisId"
                    type="text"
                    value={userWellisId}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                      setUserWellisId(val);
                      setWellisIdAvailable(null);
                      setWellisIdError('');
                    }}
                    maxLength="10"
                    className="flex-1 bg-transparent text-on-surface text-body-sm focus:outline-none placeholder-on-background/40"
                    placeholder="XXXXXXXXXX"
                    required
                  />
                </div>
                {userWellisId && wellisIdAvailable && (
                  <p className="text-body-xs text-success mt-1">✓ {t('auth.signup.wellisIdAvailable')}</p>
                )}
                {wellisIdError && (
                  <p className="text-body-xs text-error mt-1">{wellisIdError}</p>
                )}
              </div>
              <button
                type="button"
                onClick={checkWellisIdUniqueness}
                disabled={!userWellisId || userWellisId.length !== 10 || checkingWellisId}
                className="px-4 py-2 rounded-lg font-medium text-body-sm transition-colors bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
              >
                {checkingWellisId ? t('auth.signup.checking') : t('auth.signup.checkUniqueness')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="block text-body-sm text-on-background mb-1">
                {t('auth.signup.firstName')} <span className="text-error">*</span>
                <FieldCheck ok={!!firstName.trim()} />
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
                placeholder={t('auth.signup.firstNamePlaceholder')}
                required
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-body-sm text-on-background mb-1">
                {t('auth.signup.lastName')}
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
                placeholder={t('auth.signup.lastNamePlaceholder')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="email" className="block text-body-sm text-on-background mb-1">
                {t('auth.signup.email')}
              </label>
              <input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
                placeholder={t('auth.signup.emailPlaceholder')}
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-body-sm text-on-background mb-1">
                {t('auth.signup.phone')}
              </label>
              <input
                id="phone"
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
                placeholder={t('auth.signup.phonePlaceholder')}
              />
            </div>
          </div>

          <p className="text-body-xs text-on-background/60">
            {t('auth.signup.emailOrPhoneNote')}
            <FieldCheck ok={!!email.trim() || !!phone.trim()} />
          </p>

          <div>
            <label htmlFor="password" className="block text-body-sm text-on-background mb-1">
              {t('auth.signup.password')}
              <FieldCheck ok={passwordStrength && passwordStrength !== 'weak'} />
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-2 pr-10 bg-surface border rounded-lg text-on-surface text-body-sm focus:outline-none ${
                  password && !validatePasswordStrength(password).includes('weak')
                    ? 'border-success focus:border-success'
                    : 'border-outline focus:border-primary'
                }`}
                placeholder="••••••••"
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-background/60 hover:text-on-background transition-colors"
                title={showPassword ? t('auth.signup.hidePassword') : t('auth.signup.showPassword')}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-2 flex-1 rounded ${getPasswordStrengthColor()}`}></div>
                  <span className="text-label-sm text-on-background/60">{getPasswordStrengthText()}</span>
                </div>
                <p className="text-label-sm text-on-background/50">
                  {t('auth.signup.passwordRequirements')}
                </p>
              </div>
            )}
            {!password && (
              <p className="text-label-sm text-on-background/50 mt-1">{t('auth.signup.minCharacters')}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-body-sm text-on-background mb-1">
              {t('auth.signup.confirmPassword')}
              <FieldCheck ok={!!confirmPassword && passwordsMatch} />
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-2 pr-10 bg-surface border rounded-lg text-on-surface text-body-sm focus:outline-none ${
                  confirmPassword && passwordsMatch
                    ? 'border-success focus:border-success'
                    : confirmPassword && !passwordsMatch
                    ? 'border-error focus:border-error'
                    : 'border-outline focus:border-primary'
                }`}
                placeholder="••••••••"
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-background/60 hover:text-on-background transition-colors"
                title={showConfirmPassword ? t('auth.signup.hidePassword') : t('auth.signup.showPassword')}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {confirmPassword && (
              <p className={`text-label-sm mt-1 ${passwordsMatch ? 'text-success' : 'text-error'}`}>
                {passwordsMatch ? t('auth.signup.passwordsMatch') : t('auth.signup.passwordsDontMatch')}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={
              loading
              || !affiliateName
              || validatingAffiliate
              || !firstName.trim()
              || !userWellisId.trim()
              || userWellisId.length !== 10
              || !wellisIdAvailable
              || (!email.trim() && !phone.trim())
              || !passwordsMatch
              || !passwordStrength
              || passwordStrength === 'weak'
              || checkingWellisId
            }
            className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-body-md"
            title={
              !affiliateName
                ? t('auth.signup.validAffiliateFirst')
              : validatingAffiliate
                ? t('auth.signup.validatingAffiliate')
              : !firstName.trim()
                ? t('auth.signup.firstNameRequired')
              : !userWellisId.trim()
                ? t('auth.signup.wellisIdRequired')
              : userWellisId.length !== 10
                ? t('auth.signup.wellisIdFormat')
              : !wellisIdAvailable
                ? t('auth.signup.checkWellisIdFirst')
              : (!email.trim() && !phone.trim())
                ? t('auth.signup.emailOrPhoneRequired')
              : !passwordsMatch
                ? t('auth.signup.passwordMismatch')
              : !passwordStrength
                ? t('auth.signup.passwordTooShort')
              : passwordStrength === 'weak'
                ? t('auth.signup.passwordTooShort')
              : checkingWellisId
                ? t('auth.signup.checking')
                : ''
            }
          >
            {loading ? t('auth.signup.signingUp') : t('auth.signup.signupButton')}
          </button>
        </form>

        <p className="text-body-sm text-on-background/60 text-center mt-6">
          {t('auth.signup.haveAccount')}{' '}
          <a href="/login" className="text-primary font-medium hover:underline">
            {t('auth.signup.login')}
          </a>
        </p>
      </div>
    </div>
  );
}
